import React from 'react';
import {Box, Button, Card, CardContent, Chip, Stack, Typography,} from '@mui/material';
import {AccessTime as AccessTimeIcon, Schedule as ScheduleIcon,} from '@mui/icons-material';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {Dayjs} from 'dayjs';
import 'dayjs/locale/de';

interface PickerWithNowProps {
    label: string;
    value: Dayjs | null;
    onChange: (value: Dayjs | null) => void;
    onNow: () => void;
    disabled?: boolean;
}

const PickerWithNow: React.FC<PickerWithNowProps> = ({
                                                         label,
                                                         value,
                                                         onChange,
                                                         onNow,
                                                         disabled = false
                                                     }) => (
    <Box display='flex' alignItems='center' gap={1}>
        <DateTimePicker
            label={label}
            value={value}
            disabled={disabled}
            onAccept={(newValue) => {
                if (newValue?.isValid() && !disabled) onChange(newValue);
            }}
            slotProps={{
                textField: {size: 'small', sx: {minWidth: 180}},
                actionBar: {actions: ['accept']},
            }}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            openTo='day'
        />
        <Button
            variant='outlined'
            size='small'
            onClick={onNow}
            disabled={disabled}
            sx={{marginTop: '-15px', paddingTop: '6px'}}>
            Jetzt
        </Button>
    </Box>
);

interface TimeTrackingProps {
    editMode: boolean;
    getCurrentTimeFrom: () => Dayjs | null;
    getCurrentTimeTo: () => Dayjs | null;
    getTotalDuration: () => { hours: number; minutes: number; percentOfDay: number } | null;
    setWtFrom: (time: Dayjs | null) => void;
    setWtTo: (time: Dayjs | null) => void;
    handleFieldUpdate: (field: string, value: string) => void;
}

export const TimeTracking: React.FC<TimeTrackingProps> = ({
                                                              editMode,
                                                              getCurrentTimeFrom,
                                                              getCurrentTimeTo,
                                                              getTotalDuration,
                                                              setWtFrom,
                                                              setWtTo,
                                                              handleFieldUpdate,
                                                          }) => {
    const duration = getTotalDuration();

    return (
        <Card
            id={'time-tracking-task'}
            variant='outlined'
            sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: 3,
                borderColor: 'transparent',
                background: (theme) => theme.palette.background.paper,
            }}>
            <CardContent sx={{p: 3}}>
                {/* Header mit Farbakzent */}
                <Box
                    display='flex'
                    alignItems='center'
                    sx={{
                        mb: 2,
                        position: 'relative',
                    }}>
                    <ScheduleIcon color='primary' sx={{mr: 1, fontSize: 28}}/>
                    <Typography variant='h6' sx={{fontWeight: 600}}>
                        Arbeitszeit
                    </Typography>
                </Box>

                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
                    <Box display='flex' gap={2} flexWrap='wrap' alignItems='center'>
                        {/* Von */}
                        <PickerWithNow
                            label='Von'
                            value={getCurrentTimeFrom()}
                            disabled={!editMode}
                            onChange={(nv) => {
                                setWtFrom(nv);
                                if (nv) {
                                    handleFieldUpdate('time_from', nv.format('YYYY-MM-DDTHH:mm:ss'));
                                }
                            }}
                            onNow={() => {
                                const now = new Date();
                                const nowDayjs = require('dayjs')(now);
                                setWtFrom(nowDayjs);
                                handleFieldUpdate('time_from', nowDayjs.format('YYYY-MM-DDTHH:mm:ss'));
                            }}
                        />

                        {/* Bis */}
                        <PickerWithNow
                            label='Bis'
                            value={getCurrentTimeTo()}
                            disabled={!editMode}
                            onChange={(nv) => {
                                setWtTo(nv);
                                if (nv) {
                                    handleFieldUpdate('time_to', nv.format('YYYY-MM-DDTHH:mm:ss'));
                                }
                            }}
                            onNow={() => {
                                const now = new Date();
                                const nowDayjs = require('dayjs')(now);
                                setWtTo(nowDayjs);
                                handleFieldUpdate('time_to', nowDayjs.format('YYYY-MM-DDTHH:mm:ss'));
                            }}
                        />
                    </Box>

                    {/* Gesamtzeit Anzeige */}
                    {duration && (
                        <Box sx={{mt: 3}}>
                            <Stack direction='row' alignItems='center' spacing={2}>
                                <Chip
                                    icon={<AccessTimeIcon color='inherit' sx={{color: '#fff'}}/>}
                                    label={`Gesamt: ${duration.hours} Std. ${duration.minutes} Min.`}
                                    variant='filled'
                                    sx={{
                                        borderRadius: 1,
                                        fontWeight: 500,
                                        background: (theme) => theme.palette.primary.main,
                                        color: (theme) => theme.palette.primary.contrastText,
                                    }}
                                />
                            </Stack>
                        </Box>
                    )}
                </LocalizationProvider>
            </CardContent>
        </Card>
    );
};
